/**
 * Taco Slap on screen. The engine (engine.ts) decides right and wrong; this file turns
 * taps, holds and swipes into actions, animates cards, and keeps the clock honest.
 *
 * Inputs
 *   word buttons (bottom row) → say that word
 *   tap the pile             → slap
 *   double-tap the pile      → gorilla (beat your chest)
 *   press & hold the pile    → octopus (all hands on the table)
 *   swipe up from the pile   → unicorn (horn up)
 *   keys: 1–5 say · space tap/hold · ↑ swipe · esc pause
 */
import { TacoSlap, CHANT, isSpecial, formatTime, type Action, type CardKind, type Outcome, type Special, type Word } from './engine';
import type { GameContext } from '../registry';
import { el, toast } from '../../ui/dom';
import { showResult } from '../../ui/result';
import { tacoSlapDef } from './def';

export const EMOJI: Record<CardKind, string> = { taco: '🌮', cat: '🐱', goat: '🐐', cheese: '🧀', pizza: '🍕', gorilla: '🦍', octopus: '🐙', unicorn: '🦄' };
export const LABEL: Record<CardKind, string> = { taco: 'taco', cat: 'cat', goat: 'goat', cheese: 'cheese', pizza: 'pizza', gorilla: 'gorilla', octopus: 'octopus', unicorn: 'unicorn' };
export const GESTURE: Record<Special, { verb: string; hint: string }> = {
  gorilla: { verb: 'beat your chest', hint: 'double-tap' },
  octopus: { verb: 'all hands on the table', hint: 'press & hold' },
  unicorn: { verb: 'horn up', hint: 'swipe up' },
};

const HOLD_MS = 380;
const DOUBLE_MS = 400;
const SWIPE_PX = 56;
const STACK_MAX = 6;

export function mountTacoSlap(ctx: GameContext): () => void {
  const { root, sfx, buzz, player } = ctx;
  const game = new TacoSlap();
  let raf = 0;
  let running = false;
  let lastClock = '';
  const timers = new Set<ReturnType<typeof setTimeout>>();
  const later = (fn: () => void, ms: number) => {
    const t = setTimeout(() => {
      timers.delete(t);
      fn();
    }, ms);
    timers.add(t);
    return t;
  };

  // ---- DOM -----------------------------------------------------------------
  const clockEl = el('div', { class: 'ts-clock' }, '0.0s');
  const mistakesEl = el('div', { class: 'ts-mist', title: 'mistakes' }, '✕ 0');
  const deckCount = el('b', {}, '0');
  const deckEl = el('div', { class: 'ts-deck', 'aria-label': 'your pile' }, el('div', { class: 'card back' }), deckCount, el('span', {}, 'yours'));
  const windowFill = el('div', { class: 'fill' });
  const stack = el('div', { class: 'ts-stack' });
  const pileEl = el('div', { class: 'ts-pile', tabindex: '-1' }, stack, el('div', { class: 'ts-window' }, windowFill));
  const shedCount = el('b', {}, '0');
  const shedEl = el('div', { class: 'ts-shed' }, shedCount, el('span', {}, 'gone'));
  const chantEl = el('div', { class: 'ts-chant' + (player.practice ? ' show' : '') }, ...CHANT.map((w) => el('span', { class: 'w', 'data-word': w }, w)));
  const wordBtns = CHANT.map((w) =>
    el('button', { class: `word w-${w}`, type: 'button', 'data-word': w, 'aria-label': `say ${w}` }, el('span', { class: 'emoji' }, EMOJI[w]), el('small', {}, w)),
  );
  const wordsEl = el('div', { class: 'ts-words' }, ...wordBtns);
  const overlay = el('div', { class: 'ts-overlay' });
  const wrap = el(
    'section',
    { class: 'ts' },
    el('header', { class: 'ts-top' }, el('button', { class: 'icon-btn', type: 'button', 'aria-label': 'pause', onclick: () => togglePause() }, '❚❚'), clockEl, mistakesEl),
    el('div', { class: 'ts-table' }, deckEl, pileEl, shedEl),
    chantEl,
    wordsEl,
    overlay,
  );
  root.append(wrap);

  // ---- rendering -------------------------------------------------------------
  function cardEl(kind: CardKind): HTMLElement {
    const c = el('div', { class: `card face k-${kind}` }, el('span', { class: 'emoji' }, EMOJI[kind]), el('small', { class: 'name' }, LABEL[kind]));
    if (isSpecial(kind)) {
      c.classList.add('special');
      if (player.gestureHints) c.append(el('small', { class: 'gesture' }, GESTURE[kind].hint));
    }
    c.style.setProperty('--rot', `${(Math.random() * 16 - 8).toFixed(1)}deg`);
    c.style.setProperty('--dx', `${(Math.random() * 10 - 5).toFixed(1)}px`);
    return c;
  }

  function showCard(kind: CardKind): void {
    const c = cardEl(kind);
    c.classList.add('flip-in');
    stack.append(c);
    while (stack.children.length > STACK_MAX) stack.firstElementChild?.remove();
    sfx.flip();
    syncHud();
  }

  function syncHud(): void {
    const s = game.snapshot();
    deckCount.textContent = String(s.deck);
    shedCount.textContent = String(s.shed);
    mistakesEl.textContent = `✕ ${s.mistakes}`;
    deckEl.classList.toggle('empty', s.deck === 0);
    for (const w of chantEl.children) w.classList.toggle('on', (w as HTMLElement).dataset.word === game.word);
  }

  function clearStack(kind: 'fly-out' | 'fall'): void {
    const cards = Array.from(stack.children) as HTMLElement[];
    cards.forEach((c, i) => {
      c.classList.remove('flip-in');
      c.style.setProperty('--i', String(i));
      c.classList.add(kind);
    });
    later(() => cards.forEach((c) => c.remove()), 420);
  }

  function explain(o: Outcome): string {
    if (o.did === 'timeout') return 'Too slow!';
    const e = o.expected;
    if (e.type === 'say') return `No match — the word was ${e.word.toUpperCase()} ${EMOJI[e.word]}`;
    if (e.type === 'slap') return `That was a match — slap it!`;
    return `${LABEL[e.special]} ${EMOJI[e.special]}: ${GESTURE[e.special].verb} (${GESTURE[e.special].hint})`;
  }

  function handle(o: Outcome, action?: Action): void {
    cancelGesture();
    if (o.ok) {
      if (o.expected.type === 'say') {
        sfx.tick();
        buzz.light();
        const btn = wordBtns.find((b) => b.dataset.word === (action as { word: Word } | undefined)?.word);
        btn?.classList.remove('hit');
        void btn?.offsetWidth;
        btn?.classList.add('hit');
      } else {
        sfx.slap();
        buzz.success();
        clearStack('fly-out');
        pileEl.classList.remove('bump');
        void pileEl.offsetWidth;
        pileEl.classList.add('bump');
        toast(wrap, o.shed >= 4 ? `💥 ${o.shed} gone!` : `+${o.shed} gone`, 'ok', 900);
      }
    } else {
      sfx.buzz();
      buzz.error();
      clearStack('fall');
      deckEl.classList.remove('shake');
      void deckEl.offsetWidth;
      deckEl.classList.add('shake');
      const pen = el('div', { class: 'ts-penalty' }, `+${(game.rules.penaltyMs / 1000).toFixed(1)}s`);
      clockEl.append(pen);
      later(() => pen.remove(), 900);
      toast(wrap, explain(o), 'bad', 1800);
    }
    if (o.won) {
      void finish();
    } else if (game.currentCard) {
      showCard(game.currentCard);
    }
  }

  function act(action: Action): void {
    if (!running || game.paused) return;
    const o = game.act(action, performance.now());
    if (o) handle(o, action);
  }

  // ---- gestures on the pile ----------------------------------------------------
  let down: { x: number; y: number; t: number; id: number } | null = null;
  let holdTimer: ReturnType<typeof setTimeout> | null = null;
  let holdFired = false;
  let armed: ReturnType<typeof setTimeout> | null = null; // first beat of a gorilla double-tap

  function cancelGesture(): void {
    if (holdTimer) clearTimeout(holdTimer), (holdTimer = null);
    if (armed) clearTimeout(armed), (armed = null);
    holdFired = false;
    down = null;
    pileEl.classList.remove('holding', 'armed');
  }

  function pressStart(x: number, y: number, id: number): void {
    if (!running || game.paused || !game.currentCard) return;
    down = { x, y, t: performance.now(), id };
    holdFired = false;
    pileEl.classList.add('holding');
    holdTimer = setTimeout(() => {
      holdTimer = null;
      holdFired = true;
      pileEl.classList.remove('holding');
      act({ type: 'gesture', special: 'octopus' });
    }, HOLD_MS);
  }

  function pressMove(x: number, y: number): void {
    if (!down || holdFired) return;
    if (down.y - y > SWIPE_PX && Math.abs(x - down.x) < SWIPE_PX * 1.5) {
      if (holdTimer) clearTimeout(holdTimer), (holdTimer = null);
      down = null;
      pileEl.classList.remove('holding');
      act({ type: 'gesture', special: 'unicorn' });
    }
  }

  function pressEnd(): void {
    if (!down) return;
    down = null;
    pileEl.classList.remove('holding');
    if (holdFired) {
      holdFired = false;
      return;
    }
    if (holdTimer) clearTimeout(holdTimer), (holdTimer = null);
    tap();
  }

  function tap(): void {
    if (!running || game.paused) return;
    if (game.currentCard === 'gorilla') {
      if (armed) {
        clearTimeout(armed);
        armed = null;
        pileEl.classList.remove('armed');
        act({ type: 'gesture', special: 'gorilla' });
      } else {
        sfx.thump();
        buzz.light();
        pileEl.classList.add('armed');
        armed = setTimeout(() => {
          armed = null;
          pileEl.classList.remove('armed');
          act({ type: 'slap' }); // one beat is not a chest-beat: a mistake
        }, DOUBLE_MS);
      }
      return;
    }
    act({ type: 'slap' });
  }

  pileEl.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    if (down) return; // a second finger while one is down: ignore
    pileEl.setPointerCapture(e.pointerId);
    pressStart(e.clientX, e.clientY, e.pointerId);
  });
  pileEl.addEventListener('pointermove', (e) => {
    if (down && e.pointerId === down.id) pressMove(e.clientX, e.clientY);
  });
  pileEl.addEventListener('pointerup', (e) => {
    if (down && e.pointerId === down.id) pressEnd();
  });
  pileEl.addEventListener('pointercancel', () => cancelGesture());
  pileEl.addEventListener('contextmenu', (e) => e.preventDefault());

  for (const b of wordBtns) {
    b.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      act({ type: 'say', word: b.dataset.word as Word });
    });
  }

  function onKey(e: KeyboardEvent): void {
    if (e.repeat) return;
    if (e.key === 'Escape') {
      togglePause();
      return;
    }
    if (!running) return;
    const n = Number(e.key);
    if (n >= 1 && n <= 5) act({ type: 'say', word: CHANT[n - 1] });
    else if (e.key === ' ') {
      e.preventDefault();
      pressStart(0, 0, -1);
    } else if (e.key === 'ArrowUp') act({ type: 'gesture', special: 'unicorn' });
  }
  function onKeyUp(e: KeyboardEvent): void {
    if (e.key === ' ') pressEnd();
  }
  window.addEventListener('keydown', onKey);
  window.addEventListener('keyup', onKeyUp);

  // ---- clock ---------------------------------------------------------------------
  function frame(now: number): void {
    if (!running) return;
    const o = game.tick(now);
    if (o) handle(o);
    if (!running) return;
    const clock = formatTime(game.elapsed(now));
    if (clock !== lastClock) {
      clockEl.firstChild!.textContent = clock;
      lastClock = clock;
    }
    const r = game.remaining(now);
    windowFill.style.transform = `scaleX(${r.toFixed(3)})`;
    windowFill.classList.toggle('low', r < 0.3);
    raf = requestAnimationFrame(frame);
  }

  // ---- start / pause / finish ----------------------------------------------------
  function ready(): void {
    overlay.replaceChildren(
      el(
        'div',
        { class: 'sheet' },
        el('h2', {}, `${tacoSlapDef.icon} Taco Slap`),
        el('p', {}, 'The chant goes ', el('b', {}, 'taco · cat · goat · cheese · pizza'), '. Tap the word for each card — unless the card ', el('b', {}, 'is'), ' the word: then slap it!'),
        el('p', { class: 'dim' }, `${EMOJI.gorilla} double-tap · ${EMOJI.octopus} press & hold · ${EMOJI.unicorn} swipe up`),
        player.practice ? el('p', { class: 'dim' }, 'Practice mode: the chant is shown, nothing is posted.') : null,
        el('button', { class: 'btn primary big', type: 'button', onclick: () => countdown() }, 'Ready'),
        el('button', { class: 'btn ghost', type: 'button', onclick: () => ctx.exit() }, 'Back'),
      ),
    );
    overlay.classList.add('show');
  }

  function countdown(): void {
    sfx.unlock();
    let n = 3;
    const big = el('div', { class: 'count' }, String(n));
    overlay.replaceChildren(big);
    const step = () => {
      n--;
      if (n <= 0) {
        overlay.classList.remove('show');
        overlay.replaceChildren();
        start();
        return;
      }
      big.textContent = String(n);
      big.classList.remove('pop');
      void big.offsetWidth;
      big.classList.add('pop');
      sfx.tick();
      later(step, 600);
    };
    sfx.tick();
    later(step, 600);
  }

  function start(): void {
    stack.replaceChildren();
    game.start(performance.now());
    running = true;
    lastClock = '';
    showCard(game.currentCard!);
    raf = requestAnimationFrame(frame);
  }

  function togglePause(): void {
    if (!running) return;
    if (game.paused) {
      overlay.classList.remove('show');
      overlay.replaceChildren();
      game.resume(performance.now());
      raf = requestAnimationFrame(frame);
    } else {
      cancelGesture();
      game.pause(performance.now());
      cancelAnimationFrame(raf);
      overlay.replaceChildren(
        el(
          'div',
          { class: 'sheet' },
          el('h2', {}, 'Paused'),
          el('p', { class: 'dim' }, `${formatTime(game.elapsed(performance.now()))} · ${game.mistakes} mistakes · ${game.snapshot().deck} cards left`),
          el('button', { class: 'btn primary big', type: 'button', onclick: () => togglePause() }, 'Resume'),
          el('button', { class: 'btn', type: 'button', onclick: () => restart() }, 'Restart'),
          el('button', { class: 'btn ghost', type: 'button', onclick: () => ctx.exit() }, 'Quit'),
        ),
      );
      overlay.classList.add('show');
    }
  }

  function restart(): void {
    running = false;
    cancelAnimationFrame(raf);
    cancelGesture();
    overlay.classList.remove('show');
    overlay.replaceChildren();
    stack.replaceChildren();
    countdown();
  }

  async function finish(): Promise<void> {
    running = false;
    cancelAnimationFrame(raf);
    const now = performance.now();
    const score = Math.round(game.elapsed(now));
    clockEl.firstChild!.textContent = formatTime(score);
    windowFill.style.transform = 'scaleX(0)';
    sfx.win();
    buzz.success();
    const result = {
      score,
      mistakes: game.mistakes,
      detail: { bestStreak: game.bestStreak, cards: game.total, rules: game.rules },
      practice: player.practice,
    };
    const { board, newBest } = await ctx.finish(result);
    showResult(overlay, {
      game: tacoSlapDef,
      result,
      board,
      newBest,
      player,
      post: () => ctx.post(result),
      again: () => restart(),
      board_: () => ctx.showBoard(),
      menu: () => ctx.exit(),
    });
  }

  function onHidden(): void {
    if (document.hidden && running && !game.paused) togglePause();
  }
  document.addEventListener('visibilitychange', onHidden);

  ctx.setBackHandler(() => {
    if (running && !game.paused) {
      togglePause();
      return true;
    }
    return false;
  });

  ready();

  return () => {
    running = false;
    cancelAnimationFrame(raf);
    cancelGesture();
    for (const t of timers) clearTimeout(t);
    window.removeEventListener('keydown', onKey);
    window.removeEventListener('keyup', onKeyUp);
    document.removeEventListener('visibilitychange', onHidden);
    ctx.setBackHandler(null);
    wrap.remove();
  };
}
